import { Add, Edit } from '@mui/icons-material'
import { Box, Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { BlockAccessPointDto, BlockSide } from '@yard/app/api'
import { FC, useMemo, useState } from 'react'
import { YardNodeHeaderTitle } from '../../atoms/YardNodeHeaderTitle'

type Props = {
  yardBlockId: string
  accessPoints: BlockAccessPointDto[]
  isEditable?: boolean
  onAdd: (blockId: string) => void
  onEdit: (blockId: string, accessPointId: string) => void
}

const SIDE_ORDER: Record<BlockSide, number> = { North: 0, East: 1, South: 2, West: 3 }

function formatSideLabel(ap: BlockAccessPointDto): string {
  const isHorizontal = ap.side === 'North' || ap.side === 'South'
  if (isHorizontal) {
    if (ap.minBay == null && ap.maxBay == null) return ap.side
    if (ap.minBay != null && ap.maxBay != null && ap.minBay === ap.maxBay)
      return `${ap.side} B${ap.minBay}`
    return `${ap.side} B${ap.minBay ?? '–'}–${ap.maxBay ?? '–'}`
  } else {
    if (ap.minRow == null && ap.maxRow == null) return ap.side
    if (ap.minRow != null && ap.maxRow != null && ap.minRow === ap.maxRow)
      return `${ap.side} R${ap.minRow}`
    return `${ap.side} R${ap.minRow ?? '–'}–${ap.maxRow ?? '–'}`
  }
}

export const YardNodeHeaderAccessPoints: FC<Props> = props => {
  const { yardBlockId, accessPoints, isEditable, onAdd, onEdit } = props
  const { t } = useTranslate()

  const [menuAnchor, setMenuAnchor] = useState<{ mouseX: number; mouseY: number } | null>(null)

  const sortedAccessPoints = useMemo(() => {
    return [...accessPoints].sort((a, b) => {
      const sideDiff = SIDE_ORDER[a.side] - SIDE_ORDER[b.side]
      if (sideDiff !== 0) return sideDiff
      const isHorizontal = a.side === 'North' || a.side === 'South'
      const aKey = (isHorizontal ? a.minBay : a.minRow) ?? Number.POSITIVE_INFINITY
      const bKey = (isHorizontal ? b.minBay : b.minRow) ?? Number.POSITIVE_INFINITY
      return aKey - bKey
    })
  }, [accessPoints])

  const label = t('accessPointsN', 'Access pts: {n}', { n: accessPoints.length })

  return (
    <>
      <YardNodeHeaderTitle
        onClick={e => {
          if (!isEditable) return
          setMenuAnchor({ mouseX: e.clientX, mouseY: e.clientY })
        }}
        displayHoverEffect={isEditable}
        title={label}
      />

      <Menu
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorReference='anchorPosition'
        anchorPosition={
          menuAnchor ? { top: menuAnchor.mouseY, left: menuAnchor.mouseX } : undefined
        }
      >
        <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
          {accessPoints.length === 0 && (
            <MenuItem disabled>
              <ListItemText
                primary={t('noAccessPoints', 'No access points configured')}
                primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
              />
            </MenuItem>
          )}

          {sortedAccessPoints.map(ap => (
            <MenuItem
              key={ap.id}
              className='nodrag'
              onClick={() => {
                setMenuAnchor(null)
                onEdit(yardBlockId, ap.id)
              }}
            >
              <ListItemIcon>
                <Edit fontSize='small' />
              </ListItemIcon>
              <ListItemText>{formatSideLabel(ap)}</ListItemText>
            </MenuItem>
          ))}
        </Box>

        {accessPoints.length > 0 && <Divider />}

        <MenuItem
          className='nodrag'
          onClick={() => {
            setMenuAnchor(null)
            onAdd(yardBlockId)
          }}
        >
          <ListItemIcon>
            <Add fontSize='small' />
          </ListItemIcon>
          <ListItemText>{t('addNew', 'Add new')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}
